import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCategories,
  fetchCollections,
  fetchConnectStripe,
  fetchCreateProduct,
  fetchCreateProfile,
  fetchDeleteProduct,
  fetchEditProducts,
  fetchEditSellerProfile,
  fetchMarkShipped,
  fetchProducts,
  fetchSellerProfile,
  fetchStatus,
  fetchTags,
  fetchShopifyProfile,
  fetchShopifyList,
  fetchMigrateInventory,
  fetchShopifyStatus,
  fetchSizes,
  fetchColours,
  fetchAddColours,
  fetchAddSizes,
  fetchShopifyAccountCheck,
  fetchOptionTypes
} from "./apithunk";
import { fetchLogout } from "../authentication/apiThunk";

const initialState = {
  tagsList: [],
  profileData: {},
  shopifyProfile: {},
  userProfile: {},
  collectionsList: [],
  categoriesList: [],
  products: [],
  sellerStripeDetails: {},
  colorStatus: [],
  shopifyList: [],
  migrateInventory: [],
  shopifyStatus: {},
  coloursList: [],
  sizesList: [],
  optionTypesList: [],
  loading: false,
  error: null,
  shopifyAccountCheck: {},
  productVariants: {}, // Store variant data for editing
};

const tagsSlice = createSlice({
  name: "tags",
  initialState,
  reducers: {
    clearTags: (state) => {
      state.tagsList = [];
    },
    clearProfileData: (state) => {
      state.profileData = {};
    },
    clearProducts: (state) => {
      state.products = [];
    },
    clearShopifyAccountCheck: (state) => {
      state.shopifyAccountCheck = {};
    },
    setProductVariant: (state, action) => {
      const { variantId, variantData } = action.payload;
      // Ensure productVariants exists
      if (!state.productVariants) {
        state.productVariants = {};
      }
      state.productVariants[variantId] = variantData;
    },
    clearProductVariant: (state, action) => {
      const { variantId } = action.payload;
      if (state.productVariants) {
        delete state.productVariants[variantId];
      }
    },
    clearAllProductVariants: (state) => {
      state.productVariants = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.loading = false;
        state.tagsList = action.payload;
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchCollections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        state.loading = false;
        state.collectionsList = action.payload;
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categoriesList = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchCreateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profileData = action.payload;
        state.userProfile = action.payload.data;
      })
      .addCase(fetchShopifyProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShopifyProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.shopifyProfile = action.payload;
      })
      .addCase(fetchCreateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchCreateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreateProduct.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchCreateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchEditProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEditProducts.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchEditProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchSellerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userProfile = action.payload?.data;
      })
      .addCase(fetchSellerProfile.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchLogout.fulfilled, (state) => {
        state.products = [];
        state.tagsList = [];
        state.profileData = {};
        state.userProfile = {};
        state.collectionsList = [];
        state.categoriesList = [];
      })
      .addCase(fetchLogout.rejected, (state) => {
        state.products = [];
        state.tagsList = [];
        state.profileData = {};
        state.userProfile = {};
        state.collectionsList = [];
        state.categoriesList = [];
      })
      .addCase(fetchConnectStripe.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConnectStripe.fulfilled, (action, state) => {
        state.loading = false;
        state.sellerStripeDetails = action.payload;
      })
      .addCase(fetchConnectStripe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchDeleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeleteProduct.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchDeleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchMarkShipped.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarkShipped.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchMarkShipped.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.colorStatus = action.payload;
      })
      .addCase(fetchStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchEditSellerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userProfile = action.payload?.data;
      })
      .addCase(fetchEditSellerProfile.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchShopifyList.fulfilled, (state, action) => {
        state.loading = false;
        state.shopifyList = action.payload;
      })
      .addCase(fetchShopifyList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchMigrateInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.shopifyList = action.payload;
      })
      .addCase(fetchMigrateInventory.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchShopifyStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.shopifyStatus = action.payload;
      })
      .addCase(fetchShopifyStatus.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchColours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchColours.fulfilled, (state, action) => {
        state.loading = false;
        state.coloursList = action.payload;
      })
      .addCase(fetchColours.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchSizes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSizes.fulfilled, (state, action) => {
        state.loading = false;
        state.sizesList = action.payload;
      })
      .addCase(fetchSizes.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchAddColours.fulfilled, (state, action) => {
        state.loading = false;
        state.coloursList = action.payload;
      })
      .addCase(fetchAddColours.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchAddSizes.fulfilled, (state, action) => {
        state.loading = false;
        state.sizesList = action.payload;
      })
      .addCase(fetchAddSizes.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchShopifyAccountCheck.fulfilled, (state, action) => {
        state.loading = false;
        state.shopifyAccountCheck = action.payload;
      })
      .addCase(fetchShopifyAccountCheck.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchOptionTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOptionTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.optionTypesList = action.payload;
      })
      .addCase(fetchOptionTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

const { actions, reducer } = tagsSlice;
const selectorTags = (state) => state.tagsList;
const ProfileData = (state) => state.profileData;

export {
  fetchTags,
  fetchCreateProfile,
  fetchShopifyProfile,
  selectorTags,
  actions,
  fetchCollections,
  fetchCategories,
  fetchProducts,
  ProfileData,
  fetchConnectStripe,
  fetchCreateProduct,
  fetchEditProducts,
  fetchMarkShipped,
  fetchStatus,
  fetchEditSellerProfile,
  fetchShopifyList,
  fetchMigrateInventory,
  fetchShopifyStatus,
  fetchColours,
  fetchSizes,
  fetchAddColours,
  fetchAddSizes,
  fetchShopifyAccountCheck,
  fetchOptionTypes
};
export default reducer;
